import React, {
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";

import PopularGamesCard from "../../cards/popularGamesCard";
import ViewAllButton from "../viewAll";
import { initPopularGamesHoverMosaic } from "../../animations";

import powerballImage from "../../../../../static/Images/powerball.png";
import revolverImage from "../../../../../static/Images/revolver.png";
import pokiesImage from "../../../../../static/Images/pookies.png";
import mineImage from "../../../../../static/Images/mine.png";
import gamesBackground from "../../../../../static/Images/games_background.png";

import { colors, LOTTERY_NAMES, LOTTERY_TYPE_IDS } from "../../../../../theme";
import {
  Path,
  getNetworkCurrency,
  DEFAULT_CURRENCY,
} from "../../../../../static/Constants";
import { safeFromWei, networkID } from "../../../../../static/utils";
import cinstance from "../../../../Service/randomcinstance";
import web3 from "../../../../Service/web3";

import { useTheme } from "@mui/material/styles";

import "./popularGames.css";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms),
    ),
  ]);
};

const isActiveLottery = (lottery) => {
  if (!lottery) return false;
  const statusId = Number(lottery.status ?? 0);
  const typeId = Number(lottery.lotteryType ?? 0);
  if (typeId === 0) {
    return statusId === 0 || statusId === 5;
  }
  if (typeId === 1) {
    return (statusId === 0 || statusId === 5) && Boolean(lottery.lotteryId);
  }
  if (typeId === 2) {
    return statusId === 0 || statusId === 3 || statusId === 5;
  }
  return statusId !== 2 && (statusId === 0 || statusId === 3 || statusId === 5);
};

const PopularGames = ({
  lotterylist1 = [],
  lotterylist2 = [],
  lotterylist3 = [],
  lotterylist4 = [],
  lotteries: propLotteries,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const hoverTilesRef = useRef(null);
  const activeHoverImageRef = useRef(null);
  const hoverAnimationIdRef = useRef(0);
  const [hoveredGame, setHoveredGame] = useState(null);

  const [fetchedLotteries, setFetchedLotteries] = useState([]);
  const [currentNetId, setCurrentNetId] = useState(networkID);

  // Fetch dynamic on-chain lotteries if not passed via props or to augment live stats
  useEffect(() => {
    let isMounted = true;

    const loadOnChainLotteries = async () => {
      try {
        if (web3?.eth?.net?.getId) {
          try {
            const netId = await withTimeout(web3.eth.net.getId(), 2000);
            if (netId && isMounted) setCurrentNetId(netId);
          } catch (e) {}
        }

        if (!cinstance?.methods?.lotteryId) return;

        const totalIdsRes = await withTimeout(
          cinstance.methods.lotteryId().call(),
          3500,
        ).catch(() => 1);

        const totalIds = Number(totalIdsRes) || 1;
        const fetchCalls = [];

        for (let i = 1; i < totalIds; i++) {
          const fetchMethod = cinstance.methods.lottery
            ? () => cinstance.methods.lottery(i).call()
            : () => cinstance.methods.lotteries(i).call();

          fetchCalls.push(withTimeout(fetchMethod(), 3500).catch(() => null));
        }

        const rawResults = (await Promise.all(fetchCalls)).filter(
          (item) => item && item.lotteryId && item.lotteryId !== "0",
        );

        const activeList = rawResults.filter(isActiveLottery);
        if (isMounted) {
          setFetchedLotteries(activeList);
        }
      } catch (err) {
        console.warn(
          "Could not fetch on-chain lotteries for PopularGames:",
          err,
        );
      }
    };

    loadOnChainLotteries();

    return () => {
      isMounted = false;
    };
  }, []);

  const currency = useMemo(
    () => getNetworkCurrency(currentNetId) || DEFAULT_CURRENCY,
    [currentNetId],
  );

  // Consolidate lotteries by type from props and on-chain fetch
  const { megaList, spinList, mineList, rocketList } = useMemo(() => {
    const all = [
      ...fetchedLotteries,
      ...(propLotteries || []),
      ...(lotterylist1 || []),
      ...(lotterylist2 || []),
      ...(lotterylist3 || []),
      ...(lotterylist4 || []),
    ];

    // Deduplicate by lotteryId and lotteryType
    const uniqueMap = new Map();
    all.forEach((item) => {
      if (item && item.lotteryId && item.lotteryId !== "0") {
        uniqueMap.set(`${item.lotteryType}-${item.lotteryId}`, item);
      }
    });

    const deduped = Array.from(uniqueMap.values());

    const mega = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.MEGABET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const spin = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.SPINBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const mine = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.MINEBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const rocket = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.ROCKETBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    return {
      megaList: mega,
      spinList: spin,
      mineList: mine,
      rocketList: rocket,
    };
  }, [
    fetchedLotteries,
    propLotteries,
    lotterylist1,
    lotterylist2,
    lotterylist3,
    lotterylist4,
  ]);

  // Compute dynamic card configuration for each game engine
  const games = useMemo(() => {
    // 1. MegaBET
    const latestMega = megaList[0];
    const maxMegaPrizeWei = megaList.reduce((max, item) => {
      const prize = Number(item.totalPrize || 0);
      return prize > max ? prize : max;
    }, 0);
    const hasMega = megaList.length > 0;
    const megaPrizeEther =
      maxMegaPrizeWei > 0 ? safeFromWei(maxMegaPrizeWei, 2) : null;

    let megaInfoLabel = "Prize Pool";
    let megaInfoValue = `0.00 ${currency}`;
    if (megaPrizeEther && Number(megaPrizeEther) > 0) {
      megaInfoLabel = "Top Prize";
      megaInfoValue = `${megaPrizeEther} ${currency}`;
    } else if (hasMega) {
      megaInfoLabel = "Active Pools";
      megaInfoValue = `${megaList.length} ${
        megaList.length === 1 ? "Live Pool" : "Live Pools"
      }`;
    }

    // 2. SpinBET
    const latestSpin = spinList[0];
    const minSpinFeeWei = spinList.reduce((min, item) => {
      const fee = Number(item.entryFee || 0);
      if (fee > 0 && (min === 0 || fee < min)) return fee;
      return min;
    }, 0);
    const maxSpinPrizeWei = spinList.reduce((max, item) => {
      const prize = Number(item.totalPrize || 0);
      return prize > max ? prize : max;
    }, 0);
    const hasSpin = spinList.length > 0;
    const spinFeeEther =
      minSpinFeeWei > 0 ? safeFromWei(minSpinFeeWei, 2) : null;
    const spinPrizeEther =
      maxSpinPrizeWei > 0 ? safeFromWei(maxSpinPrizeWei, 2) : null;

    let spinInfoLabel = "Next Spin";
    let spinInfoValue = "Ready";
    if (spinFeeEther && Number(spinFeeEther) > 0) {
      spinInfoLabel = "Entry Fee";
      spinInfoValue = `${spinFeeEther} ${currency}`;
    } else if (spinPrizeEther && Number(spinPrizeEther) > 0) {
      spinInfoLabel = "Prize Pool";
      spinInfoValue = `${spinPrizeEther} ${currency}`;
    } else if (hasSpin) {
      spinInfoLabel = "Active Pools";
      spinInfoValue = `${spinList.length} ${
        spinList.length === 1 ? "Live Pool" : "Live Pools"
      }`;
    }

    // 3. MineBET
    const latestMine = mineList[0];
    const maxMinePrizeWei = mineList.reduce((max, item) => {
      const prize = Number(item.totalPrize || 0);
      return prize > max ? prize : max;
    }, 0);
    const minMineFeeWei = mineList.reduce((min, item) => {
      const fee = Number(item.entryFee || 0);
      if (fee > 0 && (min === 0 || fee < min)) return fee;
      return min;
    }, 0);
    const hasMine = mineList.length > 0;
    const minePrizeEther =
      maxMinePrizeWei > 0 ? safeFromWei(maxMinePrizeWei, 2) : null;
    const mineFeeEther =
      minMineFeeWei > 0 ? safeFromWei(minMineFeeWei, 2) : null;

    let mineInfoLabel = "Multiplier";
    let mineInfoValue = "Up to 50x";
    if (minePrizeEther && Number(minePrizeEther) > 0) {
      mineInfoLabel = "Prize Pool";
      mineInfoValue = `${minePrizeEther} ${currency}`;
    } else if (mineFeeEther && Number(mineFeeEther) > 0) {
      mineInfoLabel = "Entry Fee";
      mineInfoValue = `${mineFeeEther} ${currency}`;
    } else if (hasMine) {
      mineInfoLabel = "Active Pools";
      mineInfoValue = `${mineList.length} ${
        mineList.length === 1 ? "Live Pool" : "Live Pools"
      }`;
    }

    // 4. RocketBET
    const latestRocket = rocketList[0];
    const maxRocketPrizeWei = rocketList.reduce((max, item) => {
      const prize = Number(item.totalPrize || 0);
      return prize > max ? prize : max;
    }, 0);
    const minRocketFeeWei = rocketList.reduce((min, item) => {
      const fee = Number(item.entryFee || 0);
      if (fee > 0 && (min === 0 || fee < min)) return fee;
      return min;
    }, 0);
    const hasRocket = rocketList.length > 0;
    const rocketPrizeEther =
      maxRocketPrizeWei > 0 ? safeFromWei(maxRocketPrizeWei, 2) : null;
    const rocketFeeEther =
      minRocketFeeWei > 0 ? safeFromWei(minRocketFeeWei, 2) : null;

    let rocketInfoLabel = "Multiplier";
    let rocketInfoValue = "Up to 100x";
    if (rocketPrizeEther && Number(rocketPrizeEther) > 0) {
      rocketInfoLabel = "Prize Pool";
      rocketInfoValue = `${rocketPrizeEther} ${currency}`;
    } else if (rocketFeeEther && Number(rocketFeeEther) > 0) {
      rocketInfoLabel = "Entry Fee";
      rocketInfoValue = `${rocketFeeEther} ${currency}`;
    } else if (hasRocket) {
      rocketInfoLabel = "Active Pools";
      rocketInfoValue = `${rocketList.length} ${
        rocketList.length === 1 ? "Live Pool" : "Live Pools"
      }`;
    }

    return [
      {
        id: "megabet",
        title: LOTTERY_NAMES.MEGABET,
        type: "JACKPOT",
        description: "Massive jackpots & transparent on-chain draws.",
        infoLabel: megaInfoLabel,
        infoValue: megaInfoValue,
        image: powerballImage,
        accentColor: colors.emerald[600],
        route: latestMega?.lotteryId
          ? `/lottery/${latestMega.lotteryId}`
          : Path.allLotteries,
      },
      {
        id: "spinbet",
        title: LOTTERY_NAMES.SPINBET,
        type: "SPINNER",
        description: "Pick a slice, spin the wheel, and watch the draw.",
        infoLabel: spinInfoLabel,
        infoValue: spinInfoValue,
        image: revolverImage,
        accentColor: colors.primary.main,
        route: Path.Spinnerlottery,
      },
      {
        id: "minebet",
        title: LOTTERY_NAMES.MINEBET,
        type: "MINES",
        description: "A faster, higher-risk format for every ticket.",
        infoLabel: mineInfoLabel,
        infoValue: mineInfoValue,
        image: mineImage,
        accentColor: colors.purple[600],
        route: latestMine?.lotteryId
          ? `/minelottery/${latestMine.lotteryId}`
          : Path.allminelottery,
      },
      {
        id: "rocketbet",
        title: LOTTERY_NAMES.ROCKETBET,
        type: "ROCKET",
        description: "High-octane rocket lottery with exponential multipliers.",
        infoLabel: rocketInfoLabel,
        infoValue: rocketInfoValue,
        image: pokiesImage,
        accentColor: colors.amber[600],
        route: latestRocket?.lotteryId
          ? `/missilelottery/${latestRocket.lotteryId}`
          : Path.allmissilelottery,
      },
    ];
  }, [megaList, spinList, mineList, rocketList, currency]);

  useLayoutEffect(() => {
    return initPopularGamesHoverMosaic({
      tileContainer: hoverTilesRef.current,
      hoveredGame,
      activeHoverImageRef,
      hoverAnimationIdRef,
    });
  }, [hoveredGame]);

  return (
    <Box
      className="popular-and-live-games-wrapper"
      sx={{ position: "relative" }}
    >
      <Box
        component="section"
        className="home-popular-games"
        sx={{
          position: "relative",
          zIndex: 5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          className="popular-games-default-background"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage: `url(${gamesBackground})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            willChange: "opacity",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.custom.colors.alpha.white[30]}, ${theme.custom.colors.alpha.white[12]})`,
            },
          }}
        />

        <Box
          ref={hoverTilesRef}
          className="popular-games-hover-background"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gridTemplateRows: "repeat(6, 1fr)",
            overflow: "hidden",
            opacity: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))",
            },
          }}
        >
          {Array.from({ length: 60 }).map((_, index) => {
            const columns = 10;
            const column = index % columns;
            const row = Math.floor(index / columns);

            return (
              <Box
                key={index}
                className="popular-games-hover-tile"
                data-column={column}
                data-row={row}
                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  minHeight: 0,
                  backgroundColor: "transparent",
                  opacity: 0,
                  transformOrigin: "center center",
                  position: "relative",
                  overflow: "hidden",
                  "--col": column,
                  "--row": row,
                  "--cols": columns,
                  "--rows": 6,
                }}
              >
                <Box
                  className="popular-games-hover-tile-inner"
                  sx={{
                    position: "absolute",
                    width: "calc(100% * var(--cols))",
                    height: "calc(100% * var(--rows))",
                    left: "calc(var(--col) * -100%)",
                    top: "calc(var(--row) * -100%)",
                    backgroundImage: "inherit",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Box
          className="popular-games-content"
          sx={(theme) => ({
            position: "relative",
            zIndex: 2,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            pt: {
              xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
              sm: theme.custom.layoutSizes.section.paddingTop,
            },
            pb: {
              xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
              sm: theme.custom.layoutSizes.section.paddingBottom,
            },
            px: {
              xs: theme.custom.layoutSizes.mobileStandard.padding.leftRight,
              sm: theme.custom["home-section-left-padding"].sm,
              md: theme.custom["home-section-left-padding"].md,
            },
          })}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              width: "100%",
              maxWidth: "1400px",
              mx: "auto",
              pt: 0,
              pb: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              className="popular-games-header"
              direction="row"
              alignItems={{ xs: "flex-start", sm: "flex-end" }}
              justifyContent="space-between"
              spacing={2}
              sx={{
                width: "100%",
                pr: { xs: 0, sm: 0 },
                mb: (theme) =>
                  theme.typography.homeSection?.headerBottomMargin || {
                    xs: 4,
                    md: 5,
                  },
              }}
            >
              <Box
                sx={(theme) =>
                  theme.typography.homeSection?.headerContainer || {}
                }
              >
                <Typography
                  component="p"
                  className="popular-games-eyebrow"
                  sx={{
                    ...(theme.typography.homeSection?.eyebrow || {}),
                    fontSize: {
                      xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.fontSize,
                      sm: undefined,
                    },
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    "&::before": {
                      ...(theme.typography.homeSection?.eyebrow?.[
                        "&::before"
                      ] || {}),
                      background: (theme) => theme.palette.primary.main,
                    },
                  }}
                >
                  DIVERSE GAMES
                </Typography>

                <Typography
                  component="h2"
                  sx={{
                    ...(theme.typography.homeSection?.heading || {}),
                    color: theme.custom?.colors?.home?.navyDark || "#0d1b36",
                  }}
                >
                  Popular Games
                </Typography>
              </Box>

              <ViewAllButton label="View All Games" to={Path.allLotteries} />
            </Stack>

            <Grid
              container
              className="popular-games-grid"
              spacing={{ xs: 1.25, md: 0 }}
              alignItems="stretch"
              sx={{
                display: { md: "grid" },
                gridTemplateColumns: { md: "repeat(4, minmax(0, 1fr))" },
                gap: { md: "24px" },
              }}
            >
              {games.map((game) => (
                <Grid
                  item
                  key={game.id || game.title}
                  xs={6}
                  sm={6}
                  md={3}
                  className="popular-games-card"
                  onMouseEnter={() => setHoveredGame(game)}
                  onMouseLeave={() => setHoveredGame(null)}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "stretch",
                    maxWidth: { md: "none !important" },
                    flexBasis: { md: "auto !important" },
                  }}
                >
                  <PopularGamesCard
                    image={game.image}
                    title={game.title}
                    type={game.type}
                    description={game.description}
                    infoLabel={game.infoLabel}
                    infoValue={game.infoValue}
                    accentColor={game.accentColor}
                    onClick={() => {
                      if (game.route) {
                        history.push(game.route);
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default PopularGames;
